import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { proxyAPI } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

function ProxyInfo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [proxy, setProxy] = useState(null);
  const [trafficData, setTrafficData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingTraffic, setLoadingTraffic] = useState(false);

  // Default to 24h for traffic
  const [duration, setDuration] = useState('24h');
  const [granularity, setGranularity] = useState('hour');

  useEffect(() => {
    fetchProxy();
  }, [id]);

  useEffect(() => {
    if (proxy && proxy.trafficApi && proxy.trafficApi.endpoint) {
      fetchTraffic();
    }
  }, [proxy, duration, granularity]);

  const fetchProxy = async () => {
    try {
      setLoading(true);
      const response = await proxyAPI.getById(id);
      setProxy(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load proxy: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchTraffic = async () => {
    try {
      setLoadingTraffic(true);
      const response = await proxyAPI.getTraffic(id, { duration, granularity });
      const data = response.data.data || response.data;
      setTrafficData(data);
    } catch (err) {
      console.error('Failed to load traffic:', err);
      setTrafficData(null);
    } finally {
      setLoadingTraffic(false);
    }
  };

  const formatBytes = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes / (1024 * 1024);
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const formatBandwidth = (bytes) => {
    if (!bytes) return '0 MB';
    const mb = bytes;
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb.toFixed(2)} MB`;
  };

  const prepareChartData = () => {
    if (!trafficData || !trafficData.products) return [];

    const allTimestamps = new Set();
    trafficData.products.forEach(product => {
      if (product.stats) {
        Object.keys(product.stats).forEach(ts => allTimestamps.add(ts));
      }
    });

    const sortedTimestamps = Array.from(allTimestamps).sort();

    return sortedTimestamps.map(timestamp => {
      const dataPoint = { timestamp };
      trafficData.products.forEach(product => {
        dataPoint[product.name] = product.stats[timestamp] || 0;
      });
      return dataPoint;
    });
  };

  // No masking - show full proxy URLs for developer convenience
  const maskProxyUrl = (url) => {
    return url;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error && !proxy) {
    return (
      <div className="card">
        <div className="alert alert-danger">{error}</div>
        <Link to="/proxies" className="btn btn-secondary">Back to Proxies</Link>
      </div>
    );
  }

  const chartData = trafficData ? prepareChartData() : [];
  const totalBandwidth = trafficData?.products?.reduce((sum, p) => sum + (p.totalBandwidth || 0), 0) || 0;
  const hasTrafficApi = proxy?.trafficApi && proxy.trafficApi.endpoint;

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <div>
            <h2>{proxy.name}</h2>
            {proxy.description && (
              <p className="text-muted">{proxy.description}</p>
            )}
          </div>
          <div className="btn-group">
            <button onClick={() => navigate(`/proxies/${id}/edit`)} className="btn btn-primary">
              Edit Proxy
            </button>
            <Link to="/proxies" className="btn btn-secondary">
              Back to Proxies
            </Link>
          </div>
        </div>

        {/* Basic Info */}
        <div className="grid grid-2 mb-4">
          <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
            <h4 style={{ marginTop: 0 }}>Proxy URL</h4>
            <code>{maskProxyUrl(proxy.proxyUrl)}</code>
          </div>

          <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
            <h4 style={{ marginTop: 0 }}>Status</h4>
            <span className={`badge ${proxy.isActive ? 'badge-success' : 'badge-secondary'}`}>
              {proxy.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>

        {/* Tags */}
        {proxy.tags && proxy.tags.length > 0 && (
          <div className="mb-4">
            <h4>Tags</h4>
            <div>
              {proxy.tags.map(tag => (
                <span key={tag} className="badge badge-secondary mr-1">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Traffic API Info */}
        {hasTrafficApi && (
          <div className="mb-4">
            <h4>Traffic API Configuration</h4>
            <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
              <p><strong>Endpoint:</strong> {proxy.trafficApi.endpoint}</p>
              <p><strong>Method:</strong> {proxy.trafficApi.method || 'GET'}</p>
              {Object.keys(proxy.trafficApi.headers || {}).length > 0 && (
                <p><strong>Headers:</strong> {Object.keys(proxy.trafficApi.headers).length} configured</p>
              )}
              {Object.keys(proxy.trafficApi.queryParams || {}).length > 0 && (
                <p><strong>Query Params:</strong> {Object.keys(proxy.trafficApi.queryParams).length} configured</p>
              )}
            </div>
          </div>
        )}

        {/* Traffic Stats */}
        {hasTrafficApi && (
          <div className="mt-4">
            <h3>Traffic Statistics</h3>

            {/* Controls */}
            <div className="grid grid-2 mb-3">
              <div className="form-group">
                <label>Duration</label>
                <select value={duration} onChange={(e) => setDuration(e.target.value)}>
                  <option value="1h">Last Hour</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                </select>
              </div>

              <div className="form-group">
                <label>Granularity</label>
                <select value={granularity} onChange={(e) => setGranularity(e.target.value)}>
                  <option value="minute">Minute</option>
                  <option value="hour">Hour</option>
                  <option value="day">Day</option>
                </select>
              </div>
            </div>

            {loadingTraffic ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Loading traffic data...</p>
              </div>
            ) : trafficData && trafficData.products ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-3 mb-4">
                  <div className="card" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>Total Bandwidth</h4>
                    <h2 style={{ margin: 0, color: '#28a745' }}>{formatBandwidth(totalBandwidth)}</h2>
                  </div>

                  {trafficData.products.map(product => (
                    <div key={product.code} className="card" style={{ backgroundColor: '#f8f9fa', padding: '1.5rem' }}>
                      <h4 style={{ margin: '0 0 0.5rem 0', color: '#6c757d' }}>{product.name}</h4>
                      <h3 style={{ margin: 0 }}>{formatBandwidth(product.totalBandwidth)}</h3>
                    </div>
                  ))}
                </div>

                {/* Chart */}
                {chartData.length > 0 && (
                  <div className="mb-4">
                    <h4>Bandwidth Over Time</h4>
                    <ResponsiveContainer width="100%" height={300}>
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="timestamp"
                          angle={-45}
                          textAnchor="end"
                          height={100}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis label={{ value: 'Bandwidth (MB)', angle: -90, position: 'insideLeft' }} />
                        <Tooltip formatter={(value) => formatBandwidth(value)} />
                        <Legend />
                        {trafficData.products.map((product, index) => {
                          const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1'];
                          return (
                            <Line
                              key={product.code}
                              type="monotone"
                              dataKey={product.name}
                              stroke={colors[index % colors.length]}
                              strokeWidth={2}
                              dot={{ r: 3 }}
                            />
                          );
                        })}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Product Table */}
                <div className="table-responsive">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Code</th>
                        <th>Total Bandwidth</th>
                        <th>Data Points</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trafficData.products.map(product => (
                        <tr key={product.code}>
                          <td>{product.name}</td>
                          <td><code>{product.code}</code></td>
                          <td>{formatBandwidth(product.totalBandwidth)}</td>
                          <td>{product.stats ? Object.keys(product.stats).length : 0} time points</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="alert alert-info">
                No traffic data available for the selected time range.
              </div>
            )}
          </div>
        )}

        {!hasTrafficApi && (
          <div className="alert alert-info mt-4">
            This proxy does not have traffic API configured.
            <button onClick={() => navigate(`/proxies/${id}/edit`)} className="btn btn-sm btn-primary" style={{ marginLeft: '1rem' }}>
              Configure Traffic API
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProxyInfo;
